import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Mail, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABELS, fmtDateTime } from "@/lib/format";
import { PageHeader, NativeSelect, Field, Badge, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { email: "", name: "", role: "organizer", sector_id: "", athlete_id: "", active: true, password: "" };

function UserDialog({ open, onOpenChange, user, sectors, athletes, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(user ? { ...EMPTY, ...user, sector_id: user.sector_id || "", athlete_id: user.athlete_id || "", password: "" } : EMPTY); }, [open, user]);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, sector_id: f.sector_id || null, athlete_id: f.role === "athlete" ? f.athlete_id || null : null, password: f.password || null };
      if (f.role === "athlete" && f.athlete_id) payload.sector_id = athletes.find((a) => a.id === f.athlete_id)?.sector_id || payload.sector_id;
      user ? await api.put(`/users/${user.id}`, payload) : await api.post("/users", payload);
      toast.success(user ? "Utente aggiornato" : "Utente creato: email di benvenuto in invio");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{user ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome e cognome"><Input required value={f.name} onChange={set("name")} data-testid="user-name-input" /></Field>
          <Field label="Email"><Input type="email" required value={f.email} onChange={set("email")} data-testid="user-email-input" /></Field>
          <Field label="Ruolo">
            <NativeSelect value={f.role} onChange={set("role")} testid="user-role-select">
              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </NativeSelect>
          </Field>
          {f.role !== "athlete" && (
            <Field label="Settore" hint={f.role === "sector_manager" ? "Obbligatorio per il responsabile di settore" : "Facoltativo"}>
              <NativeSelect value={f.sector_id} onChange={set("sector_id")} testid="user-sector-select">
                <option value="">Nessuno (nazionale)</option>
                {sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </NativeSelect>
            </Field>
          )}
          {f.role === "athlete" && (
            <Field label="Anagrafica atleta collegata">
              <NativeSelect value={f.athlete_id} onChange={set("athlete_id")} testid="user-athlete-select">
                <option value="">Seleziona…</option>
                {athletes.map((a) => <option key={a.id} value={a.id}>{a.last_name} {a.first_name} · {a.membership_number}</option>)}
              </NativeSelect>
            </Field>
          )}
          <Field label={user ? "Nuova password (lascia vuoto per non cambiarla)" : "Password iniziale (facoltativa)"}
            hint={user ? "Almeno 8 caratteri" : "Se lasci vuoto, l'utente sceglie la password dal link nell'email di benvenuto o accede con Google"}>
            <Input type="password" value={f.password} onChange={set("password")} minLength={8} data-testid="user-password-input" />
          </Field>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={set("active")} data-testid="user-active-switch" />Accesso abilitato</label>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="user-submit-btn">Salva</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const [rows, setRows] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [role, setRole] = useState("");
  const [dialog, setDialog] = useState({ open: false, user: null });
  const load = () => api.get("/users", { params: { role } }).then((r) => setRows(r.data));
  const resend = (u) => api.post(`/users/${u.id}/welcome`)
    .then(() => toast.success(`Email di benvenuto inviata a ${u.email}`)).catch((e) => toast.error(errMsg(e)));
  const { user: me } = useAuth();
  const [toDelete, setToDelete] = useState(null);
  const remove = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success(`Utente ${toDelete.email} cancellato`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setToDelete(null);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [role]);
  useEffect(() => { api.get("/sectors").then((r) => setSectors(r.data)); api.get("/athletes").then((r) => setAthletes(r.data)); }, []);

  return (
    <div>
      <PageHeader eyebrow="Gestione accessi" title="Utenti" subtitle="Crea gli account di responsabili, organizzatori e atleti. Le autorizzazioni sono verificate dal server a ogni richiesta.">
        <Button onClick={() => setDialog({ open: true, user: null })} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="users-new-btn"><Plus className="mr-2 h-4 w-4" />Nuovo utente</Button>
      </PageHeader>
      <NativeSelect value={role} onChange={setRole} className="mb-4" testid="users-role-filter">
        <option value="">Tutti i ruoli</option>
        {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </NativeSelect>
      {rows === null ? <Loading /> : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="users-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Utente</th><th className="px-4 py-3">Ruolo</th><th className="px-4 py-3">Settore</th><th className="px-4 py-3">Ultimo accesso</th><th className="px-4 py-3">Stato</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                  <td className="px-4 py-3">{ROLE_LABELS[u.role]}</td>
                  <td className="px-4 py-3 text-slate-600">{sectors.find((s) => s.id === u.sector_id)?.name || "Nazionale"}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDateTime(u.last_login)}</td>
                  <td className="px-4 py-3"><Badge cls={u.active ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}>{u.active ? "Attivo" : "Disattivato"}</Badge></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {u.active && <Button size="sm" variant="ghost" title="Reinvia email di benvenuto" onClick={() => resend(u)} data-testid={`user-welcome-btn-${u.id}`}><Mail className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => setDialog({ open: true, user: u })} data-testid={`user-edit-btn-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                    {u.id !== me.id && <Button size="sm" variant="ghost" title="Cancella utente" onClick={() => setToDelete(u)} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`user-delete-btn-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <UserDialog open={dialog.open} user={dialog.user} onOpenChange={(v) => setDialog({ ...dialog, open: v })} sectors={sectors} athletes={athletes} onSaved={load} />
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancellare l'utente {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              L'account {toDelete?.email} non potrà più accedere e verrà tolto dalle manifestazioni assegnate. L'operazione non si può annullare.
              L'eventuale anagrafica atleta, le licenze e lo storico di verifiche e partecipazioni restano invariati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="user-delete-confirm-btn">Cancella utente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
